import React from 'react';
import {
  Box,
  Typography,
  Container,
  Button,
  Grid,
  IconButton,
} from '@mui/material';
import Facebook from '@mui/icons-material/Facebook';
import Instagram from '@mui/icons-material/Instagram';

export default function Footer({ onNavigate }) {
  const logoMark = (
    <Box
      sx={{
        width: 24,
        height: 24,
        borderRadius: '50%',
        background: 'conic-gradient(from 200deg, #D4A24C, #E4572E, #2F8F76, #D4A24C)',
        display: 'grid',
        placeItems: 'center',
        position: 'relative',
        mr: 1,
        '&::after': {
          content: '""',
          width: 10,
          height: 10,
          backgroundColor: '#0F2E2B',
          borderRadius: '50%',
        },
      }}
    />
  );

  return (
    <Box
      component="footer"
      sx={{
        background: '#0F2E2B',
        borderTop: '1px solid rgba(245, 241, 230, 0.14)',
        padding: '64px 0 32px',
        color: '#F5F1E6',
        textAlign: 'left',
      }}
    >
      <Container maxWidth="lg">
        {/* Foot Grid */}
        <Grid container spacing={5} sx={{ marginBottom: '50px' }}>
          {/* Brand Info & Newsletter */}
          <Grid size={{ xs: 12, sm: 6, md: 7 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                color: 'inherit',
                fontFamily: '"Fraunces", serif',
                fontWeight: 700,
                fontSize: '21px',
                letterSpacing: '-0.01em',
                marginBottom: '14px',
              }}
            >
              {logoMark}
              iSoret
            </Box>
            <Typography
              sx={{
                color: 'rgba(245, 241, 230, 0.66)',
                fontSize: '13.5px',
                lineHeight: 1.6,
                maxWidth: '100%',
                marginBottom: '20px',
              }}
            >
              A promotional coupon marketplace with assured cashback and branded rewards. No gambling, no betting — only published, fixed-value deals.
            </Typography>

            {/* Social Media Links */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <IconButton
                component="a"
                href="https://www.facebook.com/people/Isoretofficial/61586373655025/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  backgroundColor: 'rgba(212, 162, 76, 0.12)',
                  color: '#D4A24C',
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: '#D4A24C',
                    color: '#0F2E2B',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <Facebook />
              </IconButton>
              <IconButton
                component="a"
                href="https://www.instagram.com/isoret.official"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  backgroundColor: 'rgba(212, 162, 76, 0.12)',
                  color: '#D4A24C',
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: '#D4A24C',
                    color: '#0F2E2B',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <Instagram />
              </IconButton>
            </Box>
          </Grid>

          {/* Company Column */}
          <Grid size={{ xs: 6, sm: 6, md: 2.5 }}>
            <Typography
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '11.5px',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: '#E8C078',
                marginBottom: '16px',
                fontWeight: 600,
              }}
            >
              Company
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
              <Box
                component="a"
                href="#about"
                onClick={(e) => {
                  e.preventDefault();
                  if (onNavigate) onNavigate('about');
                }}
                sx={{ fontSize: '13.5px', color: 'rgba(245, 241, 230, 0.66)', textDecoration: 'none', '&:hover': { color: '#F5F1E6' } }}
              >
                About iSoret
              </Box>
              <Box
                component="a"
                href="#coupons"
                onClick={(e) => {
                  e.preventDefault();
                  if (onNavigate) onNavigate('coupons');
                }}
                sx={{ fontSize: '13.5px', color: 'rgba(245, 241, 230, 0.66)', textDecoration: 'none', '&:hover': { color: '#F5F1E6' } }}
              >
                Coupons
              </Box>
              <Box
                component="a"
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  if (onNavigate) onNavigate('contact');
                }}
                sx={{ fontSize: '13.5px', color: 'rgba(245, 241, 230, 0.66)', textDecoration: 'none', '&:hover': { color: '#F5F1E6' } }}
              >
                Contact
              </Box>
            </Box>
          </Grid>

          {/* Support Column */}
          <Grid size={{ xs: 6, sm: 6, md: 2.5 }}>
            <Typography
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '11.5px',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: '#E8C078',
                marginBottom: '16px',
                fontWeight: 600,
              }}
            >
              Support
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
              <Box
                component="a"
                href="#faq"
                onClick={(e) => {
                  e.preventDefault();
                  if (onNavigate) onNavigate('faq');
                }}
                sx={{ fontSize: '13.5px', color: 'rgba(245, 241, 230, 0.66)', textDecoration: 'none', '&:hover': { color: '#F5F1E6' } }}
              >
                FAQ
              </Box>
              <Box
                component="a"
                href="#terms"
                onClick={(e) => {
                  e.preventDefault();
                  if (onNavigate) onNavigate('terms');
                }}
                sx={{ fontSize: '13.5px', color: 'rgba(245, 241, 230, 0.66)', textDecoration: 'none', '&:hover': { color: '#F5F1E6' } }}
              >
                Terms & Conditions
              </Box>
              <Box
                component="a"
                href="#privacy"
                onClick={(e) => {
                  e.preventDefault();
                  if (onNavigate) onNavigate('privacy');
                }}
                sx={{ fontSize: '13.5px', color: 'rgba(245, 241, 230, 0.66)', textDecoration: 'none', '&:hover': { color: '#F5F1E6' } }}
              >
                Privacy Policy
              </Box>
              <Box
                component="a"
                href="#refund"
                onClick={(e) => {
                  e.preventDefault();
                  if (onNavigate) onNavigate('refund');
                }}
                sx={{ fontSize: '13.5px', color: 'rgba(245, 241, 230, 0.66)', textDecoration: 'none', '&:hover': { color: '#F5F1E6' } }}
              >
                Refund Policy
              </Box>
              <Box
                component="a"
                href="#cancellation"
                onClick={(e) => {
                  e.preventDefault();
                  if (onNavigate) onNavigate('cancellation');
                }}
                sx={{ fontSize: '13.5px', color: 'rgba(245, 241, 230, 0.66)', textDecoration: 'none', '&:hover': { color: '#F5F1E6' } }}
              >
                Cancellation Policy
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Foot Bottom */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingTop: '26px',
            borderTop: '1px solid rgba(245, 241, 230, 0.14)',
            fontSize: '12.5px',
            color: 'rgba(245, 241, 230, 0.66)',
            flexWrap: 'wrap',
            gap: '14px',
          }}
        >
          <Typography sx={{ fontSize: '12.5px' }}>
            © 2026 iSoret. All rights reserved. Operated by FLUX INFOTECH LLP
          </Typography>
          <Typography
            sx={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '12.5px',
              fontWeight: 600,
            }}
          >
            MADE FOR THE ASSURED SHOPPER
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
